import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosBaseURL from "..";
import { useAuth, AuthContextValue } from "../../providers/AuthProvider";

export const useEntitlementByContractId = (id?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useEntitlementByContractId", id],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/redemption/getEntitlements/${id}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};

export const useRedemption = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: any) => {
      const result = await axiosBaseURL.post(`/redemption/redeem`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useRedemptionNotification = () => {
  const { authString, user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["notifications"],
    staleTime: 1000 * 60 * 2, // 2 minutes
    queryFn: async () => {
      const response = await axiosBaseURL.get("/redemption/notification", {
        headers: {
          header: `${user?.userId}/${user?.token}`,
          Authorization: authString,
        },
      });

      return response.data;
    },
  });
};

export const useMarkNotificationSeen = () => {
  const queryClient = useQueryClient();
  const { user, authString } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (messageId: number) => {
      await axiosBaseURL.post(
        "/redemption/notification/seen",
        { messageId },
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
            Authorization: authString,
          },
        }
      );
    },

    onMutate: async (messageId: number) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      const previous = queryClient.getQueryData<any[]>(["notifications"]);

      // optimistic update
      queryClient.setQueryData<any[]>(
        ["notifications"],
        (old: any[] | undefined) =>
          old?.map((notification: any) =>
            notification.messageId === messageId
              ? { ...notification, seen: 1 }
              : notification
          )
      );

      return { previous };
    },

    onError: (_, __, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["notifications"], context.previous);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useSeen = () => {
  const { refetch } = useRedemptionNotification();
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (redemption: any) => {
      const result = await axiosBaseURL.put(
        `/redemption/notification/seen/${redemption.id}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );
      refetch();
      return result;
    },
  });
};

export const useGetPayments = (options?: {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "payments",
      options?.search,
      options?.status,
      options?.page,
      options?.limit,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.limit !== undefined)
        queryParams.append("limit", options.limit.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/payment?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useMakePayment = () => {
  const { refetch } = useGetPayments();
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (
      data: {
        redemptionId: number;
        txnNo: string;
        redemptionCode: string;
        saveStatus: boolean;
      }[]
    ) => {
      const result = await axiosBaseURL.post(`/payment/create`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });
      refetch();
      return result;
    },
  });
};
